/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @version 1.18.0
 * @since 1.17.0
 */

import { Fragment } from 'react';

import { Route, RouteComponentProps } from 'react-router';
import { Redirect, Switch } from 'react-router-dom';

import Title from '../../components/common/Title/Title';
import { TableNameType } from '../../components/Table/types/tableTypes';
import withAsyncStateManagement from '../../modules/asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../modules/internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../modules/internationalization/constants';
import { ECommonTranslationPages } from '../../modules/internationalization/types/common/pages';
import { AccessComponentKeyEnum } from '../../modules/role/constants/guard/guardConstants';
import { CLIENT_ID, RoutePath } from '../../routes/constants/routesConstants';
import PrivateRoute from '../../routes/PrivateRoute';
import PageNotFound from '../ErrorPage/PageNotFound';
import AutoReportIdFormatDelivery from './components/AutoReportIdFormatDelivery';
import AutoReportIdScheduleParameters from './components/AutoReportIdScheduleParameters';
import PageAutoReportId from './components/PageAutoReportId';
import AutoReportIdTable from './components/Table/AutoReportIdTable';
import autoReportIdReducer from './reducer';
import { autoReportIdSaga } from './saga';

const PageAutoReportIdRoute = (props: RouteComponentProps<{ clientId: string }>) => {
    const clientId = props.match.params.clientId;

    return (
        <Fragment>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageAutoReportId]}/>

            <Title pageName={ECommonTranslationPages.autoReportId}/>

            <Switch>
                <Route exact path={RoutePath.autoReportId}>
                    <Redirect to={RoutePath.autoReportIdFormatDelivery.replace(CLIENT_ID, clientId)}/>
                </Route>

                <Route exact path={RoutePath.autoReportIdAccountsEdit}>
                    <PageLocale lazy fileNames={[TranslationFileNamesEnum.pagePersonalAccount ]}/>
                    <AutoReportIdTable id={clientId} tableName={TableNameType.autoReportIdAccountEdit}/>
                </Route>

                <Route exact path={RoutePath.autoReportIdSubscribersEdit}>
                    <PageLocale lazy fileNames={[TranslationFileNamesEnum.pageSubscriberMobile ]}/>
                    <AutoReportIdTable id={clientId} tableName={TableNameType.autoReportIdSubscriberEdit}/>
                </Route>

                <Route exact path={[
                    RoutePath.autoReportIdFormatDelivery,
                    RoutePath.autoReportIdScheduleParameters,
                    RoutePath.autoReportIdAccounts,
                    RoutePath.autoReportIdSubscribers,
                ]}>
                    <PageAutoReportId>
                        <Route exact path={RoutePath.autoReportIdFormatDelivery}>
                            <AutoReportIdFormatDelivery/>
                        </Route>
                        <Route exact path={RoutePath.autoReportIdScheduleParameters}>
                            <AutoReportIdScheduleParameters/>
                        </Route>
                        <Route exact path={RoutePath.autoReportIdAccounts}>
                            <AutoReportIdTable id={clientId} tableName={TableNameType.autoReportIdAccount}/>
                        </Route>
                        <Route exact path={RoutePath.autoReportIdSubscribers}>
                            <AutoReportIdTable id={clientId} tableName={TableNameType.autoReportIdSubscriber}/>
                        </Route>
                    </PageAutoReportId>
                </Route>

                <PrivateRoute
                    accessKey={AccessComponentKeyEnum.PageNotFound}
                    component={PageNotFound}
                    path="**"
                />
            </Switch>
        </Fragment>
    );
};

export default withAsyncStateManagement(
    PageAutoReportIdRoute,
    { reducer: { autoReportId: autoReportIdReducer }, sagas: autoReportIdSaga },
);
